/**
 * AllergyPicker
 * CCH Care — Phase 2.5+
 *
 * Structured multi-select allergy picker with categories.
 * Stores data as JSON: { food: [], medication: [], insect: [], skin_material: [], other: "" }
 * Serialized to/from a plain string for the allergies text column.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { COLORS } from '../../../config/constants';

// ── Allergy categories and options ───────────────────────────
export const ALLERGY_CATEGORIES = [
  {
    key: 'food',
    label: 'Food',
    icon: '🍽️',
    options: ['Peanuts', 'Tree Nuts', 'Shellfish', 'Fish', 'Dairy', 'Eggs', 'Wheat/Gluten', 'Soy'],
  },
  {
    key: 'medication',
    label: 'Medication',
    icon: '💊',
    options: ['Penicillin', 'Sulfa', 'Aspirin', 'NSAIDs', 'Codeine', 'Contrast Dye'],
  },
  {
    key: 'insect',
    label: 'Insect Stings & Bites',
    icon: '🐝',
    options: ['Bee/Wasp', 'Fire Ant'],
  },
  {
    key: 'skin_material',
    label: 'Skin/Material',
    icon: '🧤',
    options: ['Latex', 'Adhesive/Tape', 'Nickel'],
  },
];

// ── Serialization helpers ────────────────────────────────────

/**
 * Parse a stored allergies string into structured data.
 * Handles both JSON (new format) and plain text (legacy).
 */
export function parseAllergies(raw) {
  if (!raw || raw.trim() === '') return emptyAllergies();
  // Decode HTML entities that may have been introduced by sanitization
  const decoded = raw
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  try {
    const parsed = JSON.parse(decoded);
    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        food:          Array.isArray(parsed.food)          ? parsed.food          : [],
        medication:    Array.isArray(parsed.medication)    ? parsed.medication    : [],
        insect:        Array.isArray(parsed.insect)        ? parsed.insect        : [],
        skin_material: Array.isArray(parsed.skin_material) ? parsed.skin_material : [],
        other:         typeof parsed.other === 'string'    ? parsed.other         : '',
      };
    }
  } catch {
    // Legacy plain text — put it in "other"
    return { ...emptyAllergies(), other: raw.trim() };
  }
  return emptyAllergies();
}

export function serializeAllergies(data) {
  const hasAny =
    data.food.length > 0 || data.medication.length > 0 ||
    data.insect.length > 0 || data.skin_material.length > 0 ||
    data.other.trim() !== '';
  if (!hasAny) return '';
  return JSON.stringify(data);
}

export function formatAllergiesSummary(raw) {
  if (!raw || raw.trim() === '') return null;
  const data = parseAllergies(raw);
  const items = [
    ...data.food,
    ...data.medication,
    ...data.insect,
    ...data.skin_material,
    ...(data.other.trim() ? [data.other.trim()] : []),
  ];
  if (items.length === 0) return null;
  return items.join(', ');
}

function emptyAllergies() {
  return { food: [], medication: [], insect: [], skin_material: [], other: '' };
}

// ── AllergyPicker component ──────────────────────────────────

export default function AllergyPicker({ value = '', onChange }) {
  const [data, setData] = useState(() => parseAllergies(value));
  const [expandedCat, setExpandedCat] = useState(null);
  const lastSerializedRef = useRef(value);

  // Only reset when value changes from OUTSIDE (e.g. form reset after save)
  // Ignore changes that originated from our own onChange calls
  useEffect(() => {
    if (value !== lastSerializedRef.current) {
      lastSerializedRef.current = value;
      setData(parseAllergies(value));
      setExpandedCat(null);
    }
  }, [value]);

  const update = useCallback((newData) => {
    const serialized = serializeAllergies(newData);
    lastSerializedRef.current = serialized;
    setData(newData);
    onChange(serialized);
  }, [onChange]);

  function toggleOption(catKey, option) {
    const current = data[catKey] || [];
    const next = current.includes(option)
      ? current.filter(v => v !== option)
      : [...current, option];
    update({ ...data, [catKey]: next });
  }

  function setOther(text) {
    update({ ...data, other: text });
  }

  const totalSelected =
    data.food.length + data.medication.length +
    data.insect.length + data.skin_material.length +
    (data.other.trim() ? 1 : 0);

  return (
    <div>
      <div style={{
        fontSize: 12, fontWeight: 600, color: COLORS.textMuted,
        display: 'block', marginBottom: 6,
      }}>
        Allergies
        {totalSelected > 0 && (
          <span style={{
            marginLeft: 8, fontSize: 11, fontWeight: 700,
            color: COLORS.danger, background: '#fdf0f0',
            padding: '1px 6px', borderRadius: 4,
          }}>
            {totalSelected} selected
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ALLERGY_CATEGORIES.map(cat => {
          const selected = data[cat.key] || [];
          const isOpen = expandedCat === cat.key;

          return (
            <div key={cat.key} style={{
              border: `1px solid ${selected.length > 0 ? COLORS.danger : COLORS.border}`,
              borderRadius: 8, overflow: 'hidden',
              background: selected.length > 0 ? '#fdf8f8' : COLORS.surface,
            }}>
              {/* Category header */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedCat(isOpen ? null : cat.key)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpandedCat(isOpen ? null : cat.key); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 12px', cursor: 'pointer', userSelect: 'none',
                }}
              >
                <span style={{ fontSize: 16 }}>{cat.icon}</span>
                <span style={{
                  flex: 1, fontSize: 13, fontWeight: 600,
                  color: selected.length > 0 ? COLORS.danger : COLORS.text,
                }}>
                  {cat.label}
                </span>
                {selected.length > 0 && (
                  <span style={{
                    fontSize: 11, color: COLORS.danger,
                    background: '#fde8e8', padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                  }}>
                    {selected.join(', ')}
                  </span>
                )}
                <span style={{
                  fontSize: 12, color: COLORS.textMuted,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: '0.2s',
                }}>▼</span>
              </div>

              {/* Options */}
              {isOpen && (
                <div style={{
                  padding: '8px 12px 12px',
                  borderTop: `1px solid ${COLORS.border}`,
                  display: 'flex', flexWrap: 'wrap', gap: 6,
                }}>
                  {cat.options.map(opt => {
                    const checked = selected.includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => toggleOption(cat.key, opt)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 12,
                          fontWeight: 600, cursor: 'pointer',
                          background: checked ? '#fde8e8' : COLORS.bg,
                          color: checked ? COLORS.danger : COLORS.textMuted,
                          border: `1px solid ${checked ? COLORS.danger : COLORS.border}`,
                        }}
                      >
                        {checked ? '✓ ' : ''}{opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Other / free text */}
        <div style={{
          border: `1px solid ${data.other.trim() ? COLORS.danger : COLORS.border}`,
          borderRadius: 8, padding: '10px 12px',
          background: data.other.trim() ? '#fdf8f8' : COLORS.surface,
        }}>
          <label htmlFor="allergy-other" style={{
            fontSize: 12, fontWeight: 600,
            color: data.other.trim() ? COLORS.danger : COLORS.textMuted,
            display: 'block', marginBottom: 6,
          }}>
            🔤 Other (free text)
          </label>
          <input
            id="allergy-other"
            type="text"
            value={data.other}
            onChange={e => setOther(e.target.value)}
            placeholder="e.g. Latex tape, specific food brand..."
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: `1px solid ${COLORS.border}`, fontSize: 13,
              color: COLORS.text, background: COLORS.surface,
              boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
        </div>
      </div>
    </div>
  );
}
